/**
 * campaignNodeRemap — pure node-graph id remapper for campaign workflows.
 *
 * Gives every node a freshly generated id and remaps ALL internal pointers
 * through the old-id -> new-id map:
 *   - nextNodeId (trunk chain)
 *   - falseNodeId (IF_ELSE / AB_SPLIT / WAIT_FOR false branches)
 *   - SWITCH config.cases[].nextNodeId and config.defaultNextNodeId
 *
 * This is REQUIRED whenever nodes are copied between templates:
 * replaceTemplateNodes upserts on the node id, so reusing source ids would
 * re-point the SOURCE campaign's rows at the new template on save and
 * corrupt the original. (Exact failure mode of the 2026-10-23 incident.)
 *
 * Deliberately untouched:
 *   - config.targetCampaignId on ENROLL_CAMPAIGN / UNENROLL_CAMPAIGN — it
 *     references ANOTHER template, not a node in this list.
 *   - every other config key (message bodies, waits, webhooks, ...).
 *
 * Pointers to ids that are not in the list (dangling) become null.
 * Nodes are returned in orderIndex order; nodes without a numeric orderIndex
 * default to their array position.
 *
 * Shared by importCampaign (useCampaignBuilder) and duplicateTemplate
 * (api/supabase/automations).
 *
 * @param {Array<object>} nodes - camelCase Builder-shaped nodes
 *   ({ id, nodeType, subType, config, nextNodeId, falseNodeId, orderIndex })
 * @param {() => string} [generateId] - injectable for deterministic tests
 * @returns {Array<object>} new node objects with fresh ids and remapped pointers
 */
export function remapCampaignNodes(nodes, generateId = () => crypto.randomUUID()) {
  const list = Array.isArray(nodes) ? nodes : [];

  // 1) Build old-id -> new-id map
  const idMap = {};
  list.forEach((n) => {
    if (n?.id) idMap[n.id] = generateId();
  });
  const remap = (id) => (id && idMap[id] ? idMap[id] : null);

  // 2) Rebuild nodes with fresh ids + remapped pointers
  return list
    .map((n, idx) => {
      const config = { ...(n.config || {}) };
      if (n.subType === 'SWITCH') {
        if (Array.isArray(config.cases)) {
          config.cases = config.cases.map((c) => ({ ...c, nextNodeId: remap(c.nextNodeId) }));
        }
        if (config.defaultNextNodeId) {
          config.defaultNextNodeId = remap(config.defaultNextNodeId);
        }
      }
      return {
        id: idMap[n.id] || generateId(),
        nodeType: n.nodeType,
        subType: n.subType,
        config,
        nextNodeId: remap(n.nextNodeId),
        falseNodeId: remap(n.falseNodeId),
        orderIndex: typeof n.orderIndex === 'number' ? n.orderIndex : idx,
      };
    })
    .sort((a, b) => a.orderIndex - b.orderIndex);
}
